
export const UserRole = {
  STUDENT: 'student',
  PROFESSIONAL: 'professional',
  ADMIN: 'admin'
} as const;

export type UserRole = typeof UserRole[keyof typeof UserRole];

export const SubscriptionTier = {
  BASIC: 'Basic',
  STANDARD: 'Standard',
  ADVANCED: 'Advanced'
} as const;

export type SubscriptionTier = typeof SubscriptionTier[keyof typeof SubscriptionTier];

export const RequestStatus = {
  PENDING: 'pending',
  ACCEPTED: 'accepted',
  DECLINED: 'declined',
  COMPLETED: 'completed'
} as const;

export type RequestStatus = typeof RequestStatus[keyof typeof RequestStatus];

export const MeetingPlatform = {
  ZOOM: 'Zoom',
  TEAMS: 'Microsoft Teams',
  OUTLOOK: 'Outlook Calendar',
  GOOGLE: 'Google Meet'
} as const;

export type MeetingPlatform = typeof MeetingPlatform[keyof typeof MeetingPlatform];

export const SchedulingStatus = {
  NOT_STARTED: 'not_started',
  PROPOSED: 'proposed',
  COUNTER_PROPOSED: 'counter_proposed',
  CONFIRMED: 'confirmed'
} as const;

export type SchedulingStatus = typeof SchedulingStatus[keyof typeof SchedulingStatus];

export const ONTARIO_UNIVERSITIES = [
  "University of Toronto",
  "University of Waterloo",
  "Western University",
  "McMaster University",
  "Queen's University",
  "York University",
  "Toronto Metropolitan University",
  "University of Ottawa",
  "Carleton University",
  "Wilfrid Laurier University",
  "University of Guelph",
  "Brock University",
  "Trent University",
  "Ontario Tech University"
];

export const CAMPUSES: Record<string, string[]> = {
  "University of Toronto": ["St. George", "Scarborough", "Mississauga"],
  "Western University": ["Main Campus", "Brescia", "Huron", "King's"],
  "York University": ["Keele", "Glendon"],
  "Toronto Metropolitan University": ["Downtown Toronto"],
  "University of Waterloo": ["Main Campus", "Stratford", "Kitchener", "Cambridge"],
  "University of Ottawa": ["Main Campus", "Lees", "Alta Vista"],
  "Carleton University": ["Main Campus"],
  "McMaster University": ["Main Campus", "Burlington", "Waterloo", "Niagara"]
};

export interface Experience {
  role: string;
  company: string;
  location: string;
  duration: string;
}

export interface Message {
  id: string;
  senderId: string;
  text: string;
  timestamp: number;
}

export interface Feedback {
  rating: number;
  comment: string;
  timestamp: number;
}

export interface User {
  id: string;
  email: string;
  fullName: string;
  role: UserRole;
  tier: SubscriptionTier;
  createdAt: number;
  updatedAt?: number;
  lastLoginAt?: number;
}

export interface StudentProfile {
  userId: string;
  fullName: string;
  school: string;
  campus?: string;
  degree: string;
  major: string;
  yearOfStudy: string;
  gpa?: string;
  interests: string[];
  mentorshipTopics?: string[]; // New: What they want to talk about
  hobbies: string[];
  goals: string;
  industries: string[];
  city: string;
  networkingCity?: string; 
  aspiringRole?: string;
  resumeUrl?: string;
  timezone: string;
  bio: string;
  workHistory: Experience[];
  updatedAt: number;
}

export interface ProfessionalProfile {
  userId: string;
  fullName: string;
  company: string;
  title: string;
  division: string;
  gradUniversity: string;
  gradUniversityYear?: string;
  gradCampus?: string;
  highSchool: string;
  highSchoolYear?: string;
  industry: string;
  yearsExperience: number;
  workHistory: Experience[];
  topics: string[]; // What they can teach
  hobbies: string[];
  city: string;
  timezone: string;
  bio: string;
  availability: string;
  maxMeetingsPerWeek: number;
  meetingsThisWeek: number;
  points: number;
  isVerified?: boolean;
  preferredPlatform?: MeetingPlatform;
  // Mentorship Preferences
  targetStudentGPA?: string; 
  targetStudentIndustry?: string; 
  targetStudentDegree?: string; 
  updatedAt: number;
}

export interface ChatRequest {
  id: string;
  studentId: string;
  professionalId: string;
  status: RequestStatus;
  studentNote: string;
  matchRationale?: string; 
  synergyScore?: number;
  synergyThesis?: string; 
  messageDraft?: string;
  talkingPoints?: string[];
  suggestedQuestions?: string[];
  meetingPlatform?: MeetingPlatform;
  meetingLink?: string;
  scheduledTime?: number; 
  proposedTimes?: string[]; 
  schedulingStatus?: SchedulingStatus;
  followUpEnabled?: boolean;
  followUpDraft?: string;
  followUpFrequency?: '1week' | '2weeks' | '1month';
  actionItems?: string[];
  lastFollowUpAt?: number;
  declineReason?: string; 
  isQueuedForNextWeek?: boolean;
  messages: Message[];
  studentLastRead?: number;
  proLastRead?: number;
  studentFeedback?: Feedback;
  proFeedback?: Feedback;
  createdAt: number;
  updatedAt: number;
}

export interface Job {
  id: string;
  title: string;
  company: string;
  location: string;
  type: 'Full-time' | 'Co-op' | 'Internship';
  industry: string;
  description: string;
  sourceUrl: string;
  matchScore?: number;
}

export interface Event {
  id: string;
  title: string;
  organizer: string;
  date: string;
  location: string;
  type: string;
  sourceUrl: string;
}
